import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Always send cookies on any axios call
axios.defaults.withCredentials = true;

// Global 401 interceptor: if any admin call returns 401, the AuthProvider
// will pick it up on its next /me check; we just need not to crash.
let _interceptorInstalled = false;
function installAxiosInterceptor(setAdmin) {
  if (_interceptorInstalled) return;
  _interceptorInstalled = true;
  axios.interceptors.response.use(
    (resp) => resp,
    (err) => {
      const url = err?.config?.url || '';
      const status = err?.response?.status;
      // Only auto-clear session on protected admin endpoints, not the login itself
      if (status === 401 && url.includes('/api/admin/')) {
        setAdmin(false);
      }
      return Promise.reject(err);
    }
  );
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authed, object = admin user
  const [admin, setAdmin] = useState(null);

  const checkSession = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setAdmin(r.data);
    } catch {
      setAdmin(false);
    }
  }, []);

  useEffect(() => {
    installAxiosInterceptor(setAdmin);
    checkSession();
  }, [checkSession]);

  const login = useCallback(async (email, password) => {
    const r = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setAdmin(r.data.user);
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Logout failure is non-blocking: we still clear local admin state below
      // so the user is signed out client-side regardless.
      if (process.env.NODE_ENV !== 'production') {
        console.error('AuthContext.logout failed', e);
      }
    }
    setAdmin(false);
  }, []);

  return (
    <AuthContext.Provider value={{ admin, login, logout, refresh: checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
